/**
 * One site's System Alerts page — the local node's own, and the cloud's for the site picked in the
 * title bar (read through its tunnel). Same page, same records (the site's system_alerts):
 *
 *   System alerts   CPU / memory / disk too high on one of its machines, a camera not streaming
 *   System errors   a service that stopped or keeps failing
 *
 *   [System alerts 3] [System errors 1]                         [Open · On it · Fixed · All] ⟳
 *   [search]  [All · CPU · Memory · Disk · Camera]
 *   WHAT (title, message, notes) | WHERE | STATUS | STARTED | LAST SEEN | TIMES | ACTIONS
 *
 * "I'm on it" (acknowledge) stops it being passed on to more people; "Mark fixed" (resolve) asks
 * for the post-incident report — a cause and what was done. CPU, memory, disk, a service running
 * again and a camera streaming again resolve themselves.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ArrowPathIcon, CheckIcon, CircleStackIcon, CpuChipIcon, ExclamationTriangleIcon, MagnifyingGlassIcon,
  ServerStackIcon, Square3Stack3DIcon, VideoCameraSlashIcon,
} from '@heroicons/react/24/outline';
import PageTabs from '../common/PageTabs';
import Modal from '../common/Modal';
import { Callout, StatusPill } from '../common/Callout';
import ResolveReport, { type ResolveReportValue } from './ResolveReport';
import {
  alertActionsErrorText,
  causeLabel,
  durationText,
  type EscalationsApi,
  type SiteSystemAlert,
  type SystemAlertKind,
} from './alertActionsModel';

export interface SystemAlertsViewProps {
  /** The site's own API, or the cloud's proxy for one site (both already exist for Escalations). */
  api: Pick<EscalationsApi, 'systemAlerts' | 'setSystemAlertStatus'>;
  /** May acknowledge / resolve (alerts.edit). */
  canEdit: boolean;
  /** Shown instead of the list when the site cannot be read (the cloud: the site is offline). */
  unavailable?: ReactNode;
}

type Tab = 'alerts' | 'errors';
type StatusFilter = 'live' | 'open' | 'acknowledged' | 'resolved' | 'all';
type KindFilter = 'all' | Exclude<SystemAlertKind, 'service'>;

const ALERT_KINDS: { value: Exclude<SystemAlertKind, 'service'>; label: string; icon: typeof CpuChipIcon }[] = [
  { value: 'cpu', label: 'CPU', icon: CpuChipIcon },
  { value: 'memory', label: 'Memory', icon: Square3Stack3DIcon },
  { value: 'disk', label: 'Disk', icon: CircleStackIcon },
  { value: 'camera', label: 'Camera', icon: VideoCameraSlashIcon },
];
const KIND_ICON: Record<string, typeof CpuChipIcon> = { service: ServerStackIcon, cpu: CpuChipIcon, memory: Square3Stack3DIcon, disk: CircleStackIcon, camera: VideoCameraSlashIcon };
const KIND_WORD: Record<string, string> = { service: 'Service', cpu: 'CPU', memory: 'Memory', disk: 'Disk', camera: 'Camera' };
const STATUSES: { value: StatusFilter; label: string }[] = [
  { value: 'live', label: 'Not fixed' },
  { value: 'open', label: 'Waiting' },
  { value: 'acknowledged', label: 'On it' },
  { value: 'resolved', label: 'Fixed' },
  { value: 'all', label: 'All' },
];
const EDGE: Record<string, string> = { critical: 'border-l-red-500', error: 'border-l-red-500', warning: 'border-l-amber-500' };
const isLive = (r: SiteSystemAlert) => r.status !== 'resolved';
const isError = (r: SiteSystemAlert) => r.kind === 'service';

const ago = (iso: string | null) => (iso ? `${durationText(Math.max(60, Math.round((Date.now() - new Date(iso).getTime()) / 60000) * 60))} ago` : '—');
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

function Seg<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string; count?: number }[] }) {
  return (
    <div className="inline-flex max-w-full overflow-x-auto rounded-xl border border-border bg-card">
      {options.map((o, i) => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} aria-pressed={on}
            className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-[13px] transition-colors ${i ? 'border-l border-border' : ''} ${on ? 'bg-foreground font-medium text-background' : 'text-foreground hover:bg-muted'}`}>
            {o.label}
            {o.count !== undefined && <span className={`tabular-nums ${on ? 'opacity-80' : 'text-muted-foreground'}`}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

function StatusTag({ r }: { r: SiteSystemAlert }) {
  if (r.status === 'resolved') return <StatusPill tone="success">{r.resolved_how === 'auto' ? 'Recovered' : 'Fixed'}</StatusPill>;
  if (r.status === 'acknowledged') return <StatusPill tone="warning">On it</StatusPill>;
  return <StatusPill tone="danger">Waiting</StatusPill>;
}

const TH = 'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap';

export default function SystemAlertsView({ api, canEdit, unavailable }: SystemAlertsViewProps) {
  const [tab, setTab] = useState<Tab>('alerts');
  const [rows, setRows] = useState<SiteSystemAlert[] | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<StatusFilter>('live');
  const [kind, setKind] = useState<KindFilter>('all');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [fixing, setFixing] = useState<SiteSystemAlert | null>(null);
  const [report, setReport] = useState<ResolveReportValue>({ cause: '', note: '' });

  const load = useCallback(async () => {
    if (unavailable) return;
    setLoading(true);
    try {
      setRows(await api.systemAlerts('all'));
      setError('');
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not read the system alerts'));
    } finally {
      setLoading(false);
    }
  }, [api, unavailable]);
  useEffect(() => { void load(); }, [load]);
  // Fresh every 30 s while the page is open — something may have recovered by itself.
  useEffect(() => {
    if (unavailable) return undefined;
    const t = setInterval(() => { if (!document.hidden) void load(); }, 30000);
    return () => clearInterval(t);
  }, [load, unavailable]);

  const all = rows || [];
  const inTab = useMemo(() => all.filter((r) => (tab === 'errors' ? isError(r) : !isError(r))), [all, tab]);
  const liveAlerts = all.filter((r) => !isError(r) && isLive(r)).length;
  const liveErrors = all.filter((r) => isError(r) && isLive(r)).length;
  const byStatus = (s: StatusFilter) => inTab.filter((r) => (s === 'all' ? true : s === 'live' ? isLive(r) : r.status === s));
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return byStatus(status)
      .filter((r) => tab === 'errors' || kind === 'all' || r.kind === kind)
      .filter((r) => !needle || [r.title, r.message, r.machine, r.service, r.acknowledged_by, r.resolved_by].some((x) => String(x || '').toLowerCase().includes(needle)));
  }, [inTab, status, kind, q, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (row: SiteSystemAlert, action: 'acknowledge' | 'resolve') => {
    setBusy(row.id);
    try {
      const next = await api.setSystemAlertStatus(row.id, action, action === 'resolve'
        ? { ...(report.cause ? { cause: report.cause } : {}), ...(report.note.trim() ? { note: report.note.trim() } : {}) }
        : undefined);
      setRows((list) => (list || []).map((r) => (r.id === row.id ? { ...r, ...next } : r)));
      if (action === 'resolve') { setFixing(null); setReport({ cause: '', note: '' }); }
      setError('');
    } catch (err) {
      setError(alertActionsErrorText(err, `Could not ${action === 'resolve' ? 'mark it fixed' : 'acknowledge it'}`));
    } finally {
      setBusy(null);
    }
  };

  const errorsTab = tab === 'errors';
  const filtering = !!q.trim() || status !== 'live' || (!errorsTab && kind !== 'all');

  return (
    <div className="space-y-3">
      <PageTabs
        active={tab}
        onChange={(k) => setTab(k as Tab)}
        tabs={[
          { key: 'alerts', label: 'System alerts', count: rows ? liveAlerts : null },
          { key: 'errors', label: 'System errors', count: rows ? liveErrors : null },
        ]}
        actions={(
          <button type="button" onClick={() => void load()} disabled={!!unavailable}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 text-[12px] text-foreground hover:bg-muted disabled:opacity-50" title="Reload">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        )}
      />

      {unavailable ? <Callout tone="info">{unavailable}</Callout> : (
        <section className="flex min-w-0 flex-col rounded-2xl border border-border bg-card">
          <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
            <label className="relative min-w-[12rem] flex-1 sm:max-w-xs">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={errorsTab ? 'Search services, messages…' : 'Search machines, cameras, messages…'}
                className="h-9 w-full rounded-xl border border-border bg-background pl-9 pr-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none" />
            </label>
            <Seg<StatusFilter> value={status} onChange={setStatus} options={STATUSES.map((s) => ({ ...s, count: byStatus(s.value).length }))} />
            {!errorsTab && (
              <Seg<KindFilter> value={kind} onChange={setKind} options={[
                { value: 'all', label: 'Everything' },
                ...ALERT_KINDS.map((k) => ({ value: k.value, label: k.label, count: inTab.filter((r) => r.kind === k.value && (status === 'all' || (status === 'live' ? isLive(r) : r.status === status))).length })),
              ]} />
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 border-b border-border bg-red-500/10 px-4 py-2 text-[13px] text-red-600">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" /> <span className="flex-1">{error}</span>
            </div>
          )}

          <div className="min-w-0 overflow-x-auto">
            {rows === null ? (
              <div className="space-y-2 p-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />)}</div>
            ) : shown.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
                <CheckIcon className="h-8 w-8 text-green-600 opacity-60" />
                <p className="text-sm font-medium text-foreground">
                  {filtering ? 'Nothing matches these filters' : errorsTab ? 'No service errors — every service is running' : 'All clear — no system alerts'}
                </p>
                {filtering && <button type="button" onClick={() => { setQ(''); setStatus('live'); setKind('all'); }} className="text-sm text-brand">Clear filters</button>}
              </div>
            ) : (
              <table className="w-full min-w-[60rem] table-fixed text-[13px]">
                <colgroup>
                  <col />
                  <col className="w-[11rem]" />
                  <col className="w-[7rem]" />
                  <col className="w-[9rem]" />
                  <col className="w-[7.5rem]" />
                  <col className="w-[4.5rem]" />
                  <col className="w-[13rem]" />
                </colgroup>
                <thead className="bg-card">
                  <tr className="border-b border-border">
                    <th className={`${TH} pl-5`}>{errorsTab ? 'Error' : 'Alert'}</th>
                    <th className={TH}>{errorsTab ? 'Service' : 'Where'}</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Started</th>
                    <th className={TH}>Last seen</th>
                    <th className={TH}>Times</th>
                    <th className={`${TH} pr-5 text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const Icon = KIND_ICON[r.kind] || ServerStackIcon;
                    const live = isLive(r);
                    // How long the incident lasted, from the first high check to when it came back.
                    const lastedFor = r.resolved_at ? durationText(Math.max(60, Math.round((new Date(r.resolved_at).getTime() - new Date(r.first_at).getTime()) / 60000) * 60)) : '';
                    const handled = r.resolved_by || r.resolved_at
                      ? `${r.resolved_how === 'auto' ? 'Recovered by itself' : `Fixed by ${r.resolved_by}`} ${when(r.resolved_at)}${lastedFor ? ` · lasted ${lastedFor}` : ''}${r.cause ? ` · ${causeLabel(r.cause)}` : ''}${r.resolution_note ? ` · ${r.resolution_note}` : ''}`
                      : r.acknowledged_by ? `On it: ${r.acknowledged_by}` : '';
                    return (
                      <tr key={r.id} className={`border-b border-border align-top last:border-0 ${live ? '' : 'opacity-70'}`}>
                        <td className={`border-l-[3px] ${live ? EDGE[r.severity || ''] || 'border-l-border' : 'border-l-transparent'} py-3 pl-4 pr-3`}>
                          <div className="flex min-w-0 items-start gap-2.5">
                            <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${live ? 'bg-red-500/10 text-red-600' : 'bg-muted text-muted-foreground'}`}>
                              <Icon className="h-4 w-4" />
                            </span>
                            <div className="min-w-0">
                              <p className="truncate text-[14px] font-semibold text-foreground" title={r.title || undefined}>{r.title || KIND_WORD[r.kind]}</p>
                              {r.message && <p className="line-clamp-2 text-[12px] text-muted-foreground" title={r.message}>{r.message}</p>}
                              <div className="mt-1 flex flex-wrap gap-1.5">
                                {r.severity && <span className={`rounded-md px-1.5 py-px text-[11px] font-semibold uppercase ${r.severity === 'warning' ? 'bg-amber-500/10 text-amber-700' : 'bg-red-500/10 text-red-600'}`}>{r.severity}</span>}
                                {(r.escalation_level || 0) > 1 && live && <span className="rounded-md bg-amber-500/10 px-1.5 py-px text-[11px] font-medium text-amber-700">Passed on · step {r.escalation_level}</span>}
                                {r.suppressed && live && (
                                  <span className="rounded-md bg-muted px-1.5 py-px text-[11px] text-muted-foreground"
                                    title={r.suppressed === 'parent' ? 'The service that serves these cameras is down — its own alert covers them.' : 'A pause is in force — nobody was told.'}>
                                    {r.suppressed === 'parent' ? 'Held back — its service is down' : 'Paused — nobody told'}
                                  </span>
                                )}
                              </div>
                              {handled && <p className="mt-1 truncate text-[12px] text-muted-foreground" title={handled}>{handled}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <p className="truncate text-foreground" title={r.service || r.machine || undefined}>{(errorsTab ? r.service : r.machine || r.service) || '—'}</p>
                          {!errorsTab && <p className="text-[12px] text-muted-foreground">{KIND_WORD[r.kind]}</p>}
                        </td>
                        <td className="px-3 py-3"><StatusTag r={r} /></td>
                        <td className="px-3 py-3"><p className="text-foreground">{ago(r.first_at)}</p><p className="text-[12px] text-muted-foreground">{when(r.first_at)}</p></td>
                        <td className="px-3 py-3 text-foreground">{ago(r.last_at)}</td>
                        <td className="px-3 py-3 tabular-nums text-foreground" title="1 + every time it was still there after another cooldown, or happened again">{r.occurrences > 1 ? `×${r.occurrences}` : '1'}</td>
                        <td className="py-3 pl-3 pr-5">
                          {canEdit && live ? (
                            <div className="flex flex-wrap justify-end gap-1.5">
                              {r.status === 'open' && (
                                <button type="button" disabled={busy === r.id} onClick={() => void act(r, 'acknowledge')}
                                  className="inline-flex items-center gap-1 rounded-lg bg-brand px-2.5 py-1.5 text-[12px] font-medium text-white hover:opacity-90 disabled:opacity-50">
                                  <CheckIcon className="h-4 w-4" /> I&apos;m on it
                                </button>
                              )}
                              <button type="button" disabled={busy === r.id} onClick={() => { setFixing(r); setReport({ cause: '', note: '' }); }}
                                className="rounded-lg border border-border px-2.5 py-1.5 text-[12px] text-foreground hover:bg-muted disabled:opacity-50">
                                Mark fixed
                              </button>
                            </div>
                          ) : <p className="text-right text-[12px] text-muted-foreground">{live ? '' : when(r.resolved_at)}</p>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {rows && (
            <p className="border-t border-border px-4 py-2 text-[12px] text-muted-foreground">
              Acknowledging one stops it being passed on to more people. CPU, memory, disk, a service running again and a camera streaming again resolve themselves.
            </p>
          )}
        </section>
      )}

      <Modal
        open={!!fixing}
        onClose={() => setFixing(null)}
        size="md"
        title="Mark it fixed"
        subtitle={fixing ? fixing.title || KIND_WORD[fixing.kind] : undefined}
        footer={(
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setFixing(null)} className="rounded-lg border border-border px-3 py-1.5 text-[13px] text-foreground hover:bg-muted">Cancel</button>
            <button type="button" disabled={!fixing || busy === fixing.id} onClick={() => fixing && void act(fixing, 'resolve')}
              className="inline-flex items-center gap-1 rounded-lg bg-brand px-4 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50">
              <CheckIcon className="h-4 w-4" /> Mark fixed
            </button>
          </div>
        )}
      >
        <div className="p-5"><ResolveReport value={report} onChange={setReport} /></div>
      </Modal>
    </div>
  );
}
